import axios from 'axios';
import RefData from 'ref-data';
import Immutable from 'seamless-immutable';
import _lodash from 'lodash';
import moment from 'moment-timezone';

import { ApiError } from '../common/ApiUtils.js';
import { getCompatibleExperience } from '../common/utils/legacy-utils';
import { CapabilityGate, capabilities } from './Services';

/**
 * In some cases we can not change a friendlyId of the experience, so additional mapper can be in use
 */
const categoryMappers = {
  'micro-experience': 'nextgen',
};

/**
 * for the category buttons at the bottom of the app detail page
 * we need to update the label from the friendlyId to something more readable
 * NOTE: the order of execution here is ***important***.
 * we first need to replace instances of '-n-' with ' & '
 * then we need to replace instances of '-' with ' '
 * @param      {String}     category     the category string
 * @return     {String}                  the manipulated category label string
 */

const prepCategoryLabel = (category) => {
  return categoryMappers[category] || category.replace(/(-n-)/g, ' & ').replace(/-/g, ' ');
};

/**
 * extract parameters from a query string
 * @param      {String}     queryString     pass in a valid query string
 * @return     {Object}                     pass back the broken out object
 */
const extractParamsFromQueryString = (queryString) => {
  if (!queryString) {
    return false;
  }
  //this is an underscore function
  return _.chain(queryString.split('&'))
    .map(function (params) {
      var p = params.split('=');
      return [p[0], decodeURIComponent(p[1])];
    })
    .object()
    .value();
};

const validateDownload = (response) => {
  // validation
  if (response.status !== 200) {
    throw new ApiError(response.status, 'Could not download your file. Please try again.', []);
  }
  return response;
};

/**
 * Download asset from desired url e.g. Facebook, Twitter etc.
 * @param {string} url - a string source from where to download the asset source
 * @param {bool} useDirectUrl - once the asset was returned as file, we sometimes want to inject the direct source URL to its href
 */
const downloadAsset = (url, useDirectUrl = false) => {
  return axios
    .get(url, {
      responseType: 'blob',
      headers:      {
        'Access-Control-Allow-Origin': App.globalContext.serverURLsecure,
      },
    })
    .then(validateDownload)
    .then((response) => {
      const blob = response.data;
      const ext = getExtfromMimeType(response.headers['content-type']);

      // Need to create a generic filename to cope with:
      // https://www.instagram.com/p/BqPZt6gF7kU/media?size=l
      // https://pbs.twimg.com/media/DtGx1VqWkAARtIk.jpg:large
      // etc. Exmple: qcf530i0abl.jpg
      const filename = ext ? Math.random().toString(36).slice(2) + '.' + ext : Math.random().toString(36).slice(2);

      if (typeof window.navigator.msSaveBlob !== 'undefined') {
        window.navigator.msSaveBlob(blob, filename);
      } else {
        const blobURL = window.URL.createObjectURL(blob);
        const tempLink = document.createElement('a');
        tempLink.style.display = 'none';
        //if we are request to use the direct URL , we inject it as part of href , else we use the blob source
        tempLink.href = useDirectUrl ? url : blobURL;
        tempLink.setAttribute('download', filename);

        if (typeof tempLink.download === 'undefined') {
          tempLink.setAttribute('target', '_blank');
        }

        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        window.URL.revokeObjectURL(blobURL);
        return filename;
      }
    })
    .catch((error) => {
      throw new ApiError(error.status, 'Could not download your file. Please try again.', []);
    });
};

const getExtfromMimeType = (mimeType) => {
  switch (mimeType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/gif':
      return 'gif';
    case 'video/webm':
      return 'webm';
    case 'video/mp4':
      return 'mp4';
    case 'video/x-matroska':
      return 'mkv';
    case 'video/quicktime':
      return 'mov';
    default:
      return '';
  }
};

/**
 * replacement for const REFS, due to it's dependence on experience id
 * because Module Editors can be used in SD while in form editor mode and being used in the global forms library,
 * we need to make sure that the campaign container ref is unset
 */
const getRefs = () => {
  const accountId = App.globalContext.account.id,
    experienceId = getCompatibleExperience().id;
  return {
    LIBRARY:  !!accountId ? `library:${accountId}:0` : '',
    CAMPAIGN: !!accountId && !!experienceId ? `campaign:${accountId}:${experienceId}` : '',
  };
};

const getMutable = (data, opts = {}) => (Immutable.isImmutable(data) ? data.asMutable(opts) : data);

//TODO: when legacy experience workspace is deprecated, this kind of experience type differentiator can move to 'src/main/javascript/console/common-entities/workspace/workspaces/**'
/**
 * RefData.Enum.STRATEGY.ENTRY.HOURLY is NOT supported by the following experience subtype refs
 */
const UNSUPPORTED_HOURLY_SUBTYPE_REFS = [
  RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEMULTINETWORKNOFORM,
  RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKETWITTER,
  RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEINSTAGRAM,
  RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEMULTINETWORKENTRYFORM,
];

//al available entry strategies
const ENTRY_STRATEGIES = RefData.Collection.hydrateAll(RefData.Enum.STRATEGY.ENTRY);

/**
 * Filters the entry strategies this experience can show
 * @param {*} subtypeId - experience subtype id
 * @returns array of ref data objects
 */
const getEntryStrategiesByExperienceSubtype = _lodash.memoize(
  (subtypeId) => {
    const filteredEntryStrategies = ENTRY_STRATEGIES.filter((item) => {
      const isHourly = item.id === RefData.Enum.STRATEGY.ENTRY.HOURLY;
      return !isHourly || (isHourly && !UNSUPPORTED_HOURLY_SUBTYPE_REFS.includes(subtypeId));
    });

    return _lodash.sortBy(filteredEntryStrategies, 'order');
  },
  (subtypeId) => `cache-${subtypeId}`
);

/**
 * Condition 'MACRO_EXP && NEXT_GEN_EXP' needed because we have an enterprise account where both are TRUE,
 * This condition cannot be as default return
 */
const getFormatTypeByCapability = () => {
  if (CapabilityGate(capabilities.MACRO_EXP) && CapabilityGate(capabilities.NEXT_GEN_EXP)) {
    return null;
  }
  if (CapabilityGate(capabilities.MACRO_EXP)) {
    return RefData.Enum.FORMAT.DISPLAY.MACRO;
  }
  if (CapabilityGate(capabilities.NEXT_GEN_EXP)) {
    return RefData.Enum.FORMAT.DISPLAY.MICRO;
  }
};
const normalizeToGMT = (momentDateTime, timeZone) => {
  const strippedTZMoment = momentDateTime.format('YYYY-MM-DD HH:mm:ss');
  const tzDateTime = moment.tz(strippedTZMoment, timeZone);
  return moment(tzDateTime.tz('GMT'), 'YYYY-MM-DDTHH:mm:ssZ').format();
};

//Used to retrive an embed.min.js source url from the domain name
const getEmbedScriptSrc = () => {
  return `${App.globalContext.xSecureDisplayURL}/ui/ngx.embed.min.js`;
};

export {
  downloadAsset,
  extractParamsFromQueryString,
  getEmbedScriptSrc,
  getEntryStrategiesByExperienceSubtype,
  getMutable,
  getRefs,
  normalizeToGMT,
  prepCategoryLabel,
  getFormatTypeByCapability,
};
